import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../../ui-components/table/table";
import { TagColor } from "../../../../app/data/common/tagColor";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { getEventSourceOptions, getEventTypeOptions } from "../common/event-options";
import "./failed-image-logs.scss";
import "../../podRecognition/podRecognition.scss";
import { FailedImageLogsFilterModel, FailedImageLogsFilterSchema, FailedImageLogsResponseModel } from "../../../../app/data/documents/failedImageLogsModels";
import { failedImageLogsSelector, getFailedImageLogs } from "../../../../slices/documents/failedImageLogsSlice";
import DownloadButton from "../../../../ui-components/download-button/downloadButton";

const initialFilterValues: FailedImageLogsFilterModel = {
    eventId: "",
    eventTimeFrom: "",
    eventTimeTo: "",
    isPgmHalt: undefined,
    eventType: undefined,
    eventSource: undefined,
    eventDetails: ""
};
export const ProgramLogsStatuses = [
    {
        label: "failure",
        value: false
    },
    {
        label: "success",
        value: true
    }
];
export const ProgramHaltOptions = [
    {
        label: "No",
        value: false
    },
    {
        label: "Yes",
        value: true
    }
];
export const ProgramHaltVarifyOptions = [
    {
        label: "N",
        value: "No"
    },
    {
        label: "Y",
        value: "Yes"
    }
];

const columnHelper = createColumnHelper<FailedImageLogsResponseModel>();

interface FailedImageHistoryLogsProps {
    onShowRelatedEvents: (eventId: string) => void;
}

const FailedImageLogs: React.FC<FailedImageHistoryLogsProps> = ({ onShowRelatedEvents }) => {
    const failedImageLogsState = useSelector(failedImageLogsSelector);
    const filterFormRef = useRef<FormikProps<FailedImageLogsFilterModel>>(null);
    const dispatch = useDispatch();
    const [filter, setFilter] = useState<FailedImageLogsFilterModel>(initialFilterValues);
    const [eventTypeOptions, setEventTypeOptions] = useState<{ value: string; label: string }[]>([]);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ top: number, left: number, width: number, height: number } | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupHeight, setPopupHeight] = useState<number>(0);
    const columnRef = useRef<HTMLDivElement | null>(null);
    const [lastPage, setLastPage] = useState<boolean>(false);
    const tableRef = React.useRef<HTMLElement>(null);

    const triggerFilterSearch = (eventIdValue: string) => {
        onShowRelatedEvents(eventIdValue);
    };

    const handleShowMore = (event: React.MouseEvent<HTMLDivElement>, rowIndex: number, topMinus: number) => {
        const cell = event.currentTarget.getBoundingClientRect();
        setPopupPosition({
            top: cell.top + window.scrollY - topMinus,
            left: cell.left + window.scrollX - 216,
            width: cell.width,
            height: cell.height,
        });
        setExpandedRow(rowIndex);
    };

    const handleClosePopup = () => {
        setExpandedRow(null);
        setPopupPosition(null);
    };

    const handleEventSourceChange = (selected: XGSSelectOption | null | undefined) => {
        setEventTypeOptions(selected ? getEventTypeOptions(selected) : []);
    };

    const onSubmitFilter = (values: FailedImageLogsFilterModel) => {
        let filterValues = values;
        if (filterValues.eventTimeFrom) filterValues.eventTimeFrom = filterValues.eventTimeFrom.toApiDateFormat();
        if (filterValues.eventTimeTo) filterValues.eventTimeTo = filterValues.eventTimeTo.toApiDateFormat();
        setFilter(values);
        dispatch(getFailedImageLogs(values, 0));
    };

    const onListInfiniteScroll = () => {
        if (failedImageLogsState.recordsRequest?.last === failedImageLogsState.recordsRequest?.page) return;
        dispatch(getFailedImageLogs(filter, failedImageLogsState.recordsRequest ? failedImageLogsState.recordsRequest.page + 1 : 0));
    };

    const getProgramHaltOptions = () => {
        return (
            ProgramHaltOptions.map(
                (status): XGSSelectOption => ({
                    label: status.label,
                    value: status.value,
                })
            ) || []
        );
    };

    const highlightRows = failedImageLogsState.records
    .map((failedImageObj: FailedImageLogsResponseModel, index: number) =>
        failedImageObj.groupId ? index : null
    )
    .filter(index => index !== null);

    useEffect(() => {
        if (popupRef.current) {
            const height = popupRef.current.getBoundingClientRect().height;
            setPopupHeight(height);
        }
    }, [expandedRow, popupPosition]);

    useEffect(() => {
        if (failedImageLogsState.records.length === 0)
            dispatch(getFailedImageLogs(initialFilterValues, 0));
        // eslint-disable-next-line
    }, [dispatch]);

    useEffect(() => {
        if (failedImageLogsState.recordsRequest?.last === failedImageLogsState.recordsRequest?.page)
            setLastPage(true);
        else
            setLastPage(false);
        // eslint-disable-next-line
    }, [failedImageLogsState.records]);

    useEffect(() => {
        const tableElement = tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0];
        const handleScroll = () => {
            setExpandedRow(null);
        };

        const currentRef = tableElement;

        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
        // eslint-disable-next-line
    }, [tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0]]);


    const columns = [
        columnHelper.accessor("eventId", {
            id: "eventId",
            header: "Event ID",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        triggerFilterSearch(cellProps.getValue());
                    }}
                >
                    {cellProps.getValue()}
                </span>
            ),
            size: 80,
            minSize: 80
        }),
        columnHelper.accessor("eventTime", {
            id: "eventTime",
            header: "Event Time",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <div>
                        <div>
                            {cellProps.getValue() ? moment(cellProps.getValue(), "YYYY-MM-DD HH:mm:ss.SSSSSSSSS").format("MM/DD/YY") : ""}
                            <span className="text-light xgs-table__cell-subitem">
                                {moment(cellProps.getValue(), "YYYY-MM-DD HH:mm:ss.SSSSSSSSS").format("hh:mm:ss A")}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 120,
            minSize: 100
        }),
        columnHelper.accessor("imgStatus", {
            id: "imgStatus",
            header: "Status",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'success' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {ProgramLogsStatuses.find(o => o.label === cellProps.getValue())?.label}
                    </Tag>
                );
            },
            size: 40,
            minSize: 40
        }),
        columnHelper.accessor("isPgmHalt", {
            id: "isPgmHalt",
            header: "Program Halt",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'N' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {ProgramHaltVarifyOptions.find(o => o.label === cellProps.getValue())?.value}
                    </Tag>
                );
            },
            size: 60,
            minSize: 60
        }),
        columnHelper.accessor("event", {
            id: "event",
            header: "Event Type",
            size: 112,
            minSize: 112
        }),
        columnHelper.accessor("eventSource", {
            id: "eventSource",
            header: "Event Source",
            size: 112,
            minSize: 112
        }),
        columnHelper.accessor("eventDetails", {
            id: "eventDetails",
            header: "Event Details",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const showMoreVisible = value?.length ? value.length : 0 > 80;
                return (
                    <div ref={columnRef} className="xgs-failed-image-logs__event-details-cell"
                        onMouseEnter={(e) => {
                            if (showMoreVisible) {
                                handleShowMore(e, cellProps.row.index, 30);
                            }
                        }}
                        onMouseLeave={handleClosePopup}
                    >
                        {showMoreVisible ? (
                            <>
                                {value.substring(0, 80)}...
                                <div
                                    onClick={(e: any) => handleShowMore(e, cellProps.row.index, 42)}
                                    className="xgs-failed-image-logs__show-more-less-btn"
                                >
                                    Show More
                                </div>
                            </>
                        ) : (
                            <span>{value}</span>
                        )}
                    </div>
                );
            },
            size: 530,
            minSize: 530
        }),
        columnHelper.accessor("archivedPath", {
            id: "archivedPath",
            header: "Failed File",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <>
                        {cellProps.getValue() &&
                            <DownloadButton
                                title="Download File"
                                onClick={() => window.open(cellProps.getValue(), "_blank")}
                            />
                        }
                        {!cellProps.getValue() && <strong className="text-light">N/A</strong>}
                    </>
                );
            },
            size: 120,
            minSize: 120
        })
    ];


    return (
        <div className="xgs-failed-image-logs__content-primary">
            <Formik
                initialValues={initialFilterValues}
                onSubmit={onSubmitFilter}
                innerRef={filterFormRef}
                validationSchema={FailedImageLogsFilterSchema}
            >
                {(props: FormikProps<FailedImageLogsFilterModel>) => (
                    <Form>
                        <div className="xgs-failed-image-logs">
                            <div className="xgs-failed-image-logs__list__filters">
                                <XGSFormInput
                                    type="text"
                                    name="Event Number"
                                    label="Event ID:"
                                    placeholder="Failed Event ID"
                                    value={props.values.eventId}
                                    onChange={(e) => props.setFieldValue("eventId", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-failed-image-logs__list__filters__input"
                                />
                                <LabeledDateRangeInput
                                    label="Event Time:"
                                    labelMode={LabelModes.column}
                                    className="xgs-failed-image-logs__list__filters__dates"
                                    start={props.values.eventTimeFrom}
                                    end={props.values.eventTimeTo}
                                    maxDate={new Date(moment().format("MM/DD/YYYY"))}
                                    onStartChange={(v) => {
                                        props.setFieldValue("eventTimeFrom", v);
                                    }}
                                    onEndChange={(v) => {
                                        props.setFieldValue("eventTimeTo", v);
                                    }}
                                />
                                <div className="xgs-failed-image-logs__controls__item">
                                    <XGSFormSelect
                                        name="isPgmHalt"
                                        label="Program Halt:"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={getProgramHaltOptions()}
                                        isClearable
                                        controlled={true}
                                    />
                                </div>
                                <div className="xgs-failed-image-logs__controls__item">
                                    <XGSFormSelect
                                        name="eventSource"
                                        label="Event Source:"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={getEventSourceOptions()}
                                        isClearable
                                        controlled={true}
                                        value={props.values.eventSource}
                                        onValueChange={(value) => handleEventSourceChange(value)}
                                    />
                                </div>
                                <div className="xgs-failed-image-logs__controls__item">
                                    <XGSFormSelect
                                        name="eventType"
                                        label="Event Type:"
                                        placeholder="Select Source First"
                                        labelMode={LabelModes.column}
                                        className="xgs-tracking-filter__input"
                                        options={eventTypeOptions}
                                        isClearable
                                        controlled={true}
                                        value={props.values.eventType}
                                    />
                                </div>
                                <XGSFormInput
                                    type="text"
                                    name="eventDetails"
                                    label="Event Details:"
                                    placeholder="Enter Event Details"
                                    labelMode={LabelModes.column}
                                    className="xgs-failed-image-logs__list__filters__input"
                                    value={props.values.eventDetails}
                                    onChange={(e) => props.setFieldValue("eventDetails", e.currentTarget.value)}
                                />
                                <Button
                                    type="submit"
                                    theme={ButtonThemes.blue}
                                    className="xgs-failed-image-logs__list__filters__button"
                                    spinner={failedImageLogsState.requestStarted && failedImageLogsState.requestCreator === "GET_RECORDS"}
                                    disabled={!props.isValid || failedImageLogsState.requestStarted}
                                >
                                    Search
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
            {(failedImageLogsState.requestFailed && (failedImageLogsState.requestCreator === "GET_RECORDS_PORTION" || failedImageLogsState.requestCreator === "GET_RECORDS")) && (
                <XGSErrorMessage>{failedImageLogsState.requestError}</XGSErrorMessage>
            )}
            {!(failedImageLogsState.requestFailed && failedImageLogsState.requestCreator === "GET_RECORDS") && (
                <div className="xgs-failed-image-logs__list">
                    <Table
                        ref={tableRef}
                        isLoading={failedImageLogsState.requestStarted && failedImageLogsState.requestCreator === "GET_RECORDS"}
                        keepTableOnLoading
                        columns={columns}
                        data={failedImageLogsState.records}
                        rowHeight={56}
                        highlightCustomRows={highlightRows}
                        highlightRowClass="xgs-failed-image-logs__selected"
                        minTableHeight={240}
                        noResultsText="There are no entries"
                        infiniteScroll
                        infiniteScrollLoading={failedImageLogsState.requestStarted && failedImageLogsState.requestCreator === "GET_RECORDS_PORTION"}
                        infiniteScrollHasNext={!lastPage}
                        onInfiniteScroll={onListInfiniteScroll}
                        responsive
                    />
                </div>
            )}
            {expandedRow !== null && popupPosition && (
                <div
                    ref={popupRef}
                    className="xgs-failed-image-logs__event-details-popup"
                    style={{
                        position: "absolute",
                        top: popupPosition.top - popupHeight,
                        left: popupPosition.left,
                        width: popupPosition.width
                    }}
                    onMouseEnter={(e) => {
                        handleShowMore(e, Number(failedImageLogsState.records?.[expandedRow].eventId), 30);
                    }}
                    onMouseLeave={handleClosePopup}
                >
                    <div className="xgs-failed-image-logs__popup-content">
                        <p>{failedImageLogsState.records?.[expandedRow]?.eventDetails || "No details available"}</p>
                        <button
                            onClick={handleClosePopup}
                            className="xgs-failed-image-logs__show-more-less-btn"
                        >
                            Show Less <i className="fas fa-minus"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FailedImageLogs;